import { useState } from "react"
import { Button } from "@/components/ui/button"
import { usePageTitle } from "@/hooks/use-page-title"
import { cn } from "@/lib/utils"
import type { LegalBlock, LegalDocument, LegalLang } from "./types"

interface LegalPageProps {
  /** The bilingual document to render. */
  document: LegalDocument
  /** Optional meta description for the page head (English). */
  metaDescription?: string
}

/**
 * Renders a single content block (paragraph or list) in the active language.
 */
function BlockContent({ block, lang }: { block: LegalBlock; lang: LegalLang }) {
  if (block.type === "list") {
    return (
      <ul className="my-3 space-y-2 ps-6 [list-style:disc]">
        {block[lang].map((item, index) => (
          <li key={index} className="leading-7 text-neutral-600">
            {item}
          </li>
        ))}
      </ul>
    )
  }

  return <p className="my-3 leading-7 text-neutral-600">{block[lang]}</p>
}

/**
 * Shared layout for bilingual legal documents (Terms, Privacy, etc.).
 * Provides a language toggle, RTL-aware direction, a table of contents, and a
 * consistent header offset so content clears the fixed public header.
 */
export function LegalPage({ document: doc, metaDescription }: LegalPageProps) {
  const [lang, setLang] = useState<LegalLang>("en")
  const isRtl = lang === "ar"
  const helmet = usePageTitle(doc.title.en, metaDescription)

  return (
    <>
      {helmet}
      <div
        dir={isRtl ? "rtl" : "ltr"}
        className={cn(
          "h-dvh overflow-y-auto bg-gradient-to-b from-neutral-50 to-white",
          isRtl && "text-right"
        )}
      >
        <div className="mx-auto max-w-3xl px-4 pb-20 pt-12 sm:px-6 lg:px-8 lg:pt-16">
          {/* Header row: title + language toggle */}
          <div className="mb-8 border-b border-neutral-200 pb-8">
            <div className="flex items-center justify-between gap-4">
              <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                {isRtl ? "قانوني" : "Legal"}
              </span>
              <div className="flex items-center gap-1 rounded-lg border border-neutral-200 bg-white p-1">
                <Button
                  type="button"
                  size="sm"
                  variant={lang === "en" ? "default" : "ghost"}
                  className="h-7 px-3 text-xs"
                  onClick={() => setLang("en")}
                >
                  English
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={lang === "ar" ? "default" : "ghost"}
                  className="h-7 px-3 text-xs"
                  onClick={() => setLang("ar")}
                >
                  العربية
                </Button>
              </div>
            </div>
            <h1 className="mt-6 text-3xl font-bold text-neutral-900 sm:text-4xl">
              {doc.title[lang]}
            </h1>
            <p className="mt-2 text-sm text-neutral-500">
              {isRtl ? "آخر تحديث: " : "Last updated: "}
              {doc.lastUpdated[lang]}
            </p>
            <p className="mt-4 leading-7 text-neutral-600">{doc.intro[lang]}</p>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {doc.sections.map((section, index) => (
              <section key={section.id} id={section.id} className="scroll-mt-28">
                <h2 className="text-xl font-semibold text-neutral-900">
                  {index + 1}. {section.heading[lang]}
                </h2>
                {section.body.map((block, blockIndex) => (
                  <BlockContent key={blockIndex} block={block} lang={lang} />
                ))}
              </section>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
